import React, { useRef, useState } from 'react';
import BigThree from '../BirthChart/BigThree';

export const personName = (person, fallback) => (person?.name || '').trim() || fallback;

const TONE_LABEL = { easy: 'Flowing', hard: 'Friction', blend: 'Intense' };

const HOUSE_THEMES = {
  1: 'identity and first impressions',
  2: 'money, comfort, and values',
  3: 'talk, ideas, and daily contact',
  4: 'home, family, and roots',
  5: 'romance, fun, and play',
  6: 'routines, care, and health',
  7: 'partnership and commitment',
  8: 'intimacy, trust, and shared resources',
  9: 'travel, beliefs, and growth',
  10: 'career and public life',
  11: 'friendship and shared hopes',
  12: 'the private, hidden, and spiritual',
};

export const SynastryScore = ({ score }) => (
  <div className="syn-score">
    <div className="syn-score-head">
      <span className="syn-score-value">{score.value}</span>
      <div>
        <p className="birth-eyebrow">Relationship score</p>
        <h3>{score.label}{score.destiny ? ' · Destiny signs' : ''}</h3>
      </div>
    </div>
    {score.drivers?.length > 0 && (
      <ul className="syn-drivers">
        {score.drivers.map((driver) => <li key={driver}>{driver}</li>)}
      </ul>
    )}
    <p className="birth-note">
      The score adds up the strongest classic bonds between two charts: Sun, Moon, Venus, Mars, and the rising sign.
      Most couples land between 5 and 20. It is a starting point, and the reading below goes further.
    </p>
  </div>
);

export const SynastryPeople = ({ people, youLabel, themLabel }) => (
  <div className="syn-people">
    {[['you', youLabel], ['them', themLabel]].map(([side, label]) => (
      <article key={side} className="syn-person">
        <h3>{label}</h3>
        <BigThree
          placements={people[side].placements}
          timeUnknown={people[side].time_unknown}
          moonChange={people[side].moon_change}
        />
      </article>
    ))}
  </div>
);

const FIRST_CONNECTIONS = 6;

export const SynastryConnections = ({ connections, youLabel, themLabel }) => {
  const [showAll, setShowAll] = useState(false);
  if (!connections.length) {
    return <p className="birth-note">No close connections between the main planets. The reading looks at the wider picture.</p>;
  }
  const shown = showAll ? connections : connections.slice(0, FIRST_CONNECTIONS);
  return (
    <>
      <ul className="syn-connections">
        {shown.map((item) => (
          <li key={`${item.you}-${item.aspect}-${item.them}`} className={`syn-connection ${item.tone}`}>
            <div className="syn-connection-head">
              <strong>{youLabel}’s {item.you} {item.aspect.toLowerCase()} {themLabel}’s {item.them}</strong>
              <span className="syn-tone">{TONE_LABEL[item.tone] || item.tone}</span>
            </div>
            <p>{item.meaning}</p>
          </li>
        ))}
      </ul>
      {connections.length > FIRST_CONNECTIONS && (
        <button type="button" className="birth-text syn-more" onClick={() => setShowAll((value) => !value)}>
          {showAll ? 'Show the strongest only' : `Show all ${connections.length} connections`}
        </button>
      )}
    </>
  );
};

export const SynastryDetails = ({ people, overlays, youLabel, themLabel }) => {
  const dialogRef = useRef(null);
  const close = () => dialogRef.current?.close?.();
  return (
    <>
      <button type="button" className="birth-details-open" onClick={() => dialogRef.current?.showModal?.()}>
        Chart details
      </button>
      <dialog
        ref={dialogRef}
        className="birth-details"
        aria-labelledby="syn-details-title"
        onClick={(event) => {
          if (event.target === dialogRef.current) close();
        }}
      >
        <div className="birth-details-inner">
          <div className="birth-details-head">
            <h3 id="syn-details-title">Chart details</h3>
            <button type="button" className="birth-details-close" aria-label="Close" onClick={close}>×</button>
          </div>
          <SynastryPlacements people={people} youLabel={youLabel} themLabel={themLabel} />
          {overlays && (overlays.you_in_them.length > 0 || overlays.them_in_you.length > 0) && (
            <>
              <h4>Where you land in each other’s charts</h4>
              <SynastryOverlays overlays={overlays} youLabel={youLabel} themLabel={themLabel} />
            </>
          )}
        </div>
      </dialog>
    </>
  );
};

export const SynastryPlacements = ({ people, youLabel, themLabel }) => (
  <div className="syn-placement-cols">
    {[['you', youLabel], ['them', themLabel]].map(([side, label]) => (
      <div key={side}>
        <h4>{label}</h4>
        <ul className="birth-placements shared-placements">
          {people[side].placements.map((placement) => (
            <li key={placement.label}>
              <span>{placement.label}</span>
              <span>
                {placement.sign} {placement.degree}
                {placement.retrograde ? ' · R' : ''}
              </span>
            </li>
          ))}
        </ul>
      </div>
    ))}
  </div>
);

export const SynastryOverlays = ({ overlays, youLabel, themLabel }) => {
  const rows = [
    ...overlays.you_in_them.map((item) => ({ ...item, owner: youLabel, host: themLabel })),
    ...overlays.them_in_you.map((item) => ({ ...item, owner: themLabel, host: youLabel })),
  ];
  if (!rows.length) return null;
  return (
    <ul className="syn-overlays">
      {rows.map((item) => (
        <li key={`${item.owner}-${item.point}`}>
          <strong>{item.owner}’s {item.point}</strong> lands in {item.host}’s house {item.house}:{' '}
          {HOUSE_THEMES[item.house]}.
        </li>
      ))}
    </ul>
  );
};
